import React from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { BookingProvider } from "./contexts/BookingContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import AuthGuard from "./components/guards/AuthGuard";
import AdminGuard from "./components/guards/AdminGuard";
import CustomerPortalLayout from "./components/layout/CustomerPortalLayout";
import Index from "./pages/Index";
import LoginPage from "./pages/auth/LoginPage";
import CustomerDashboard from "./pages/CustomerDashboard";
import SchedulePage from "./pages/SchedulePage";
import ProfilePage from "./pages/ProfilePage";
import ServiceHistory from "./pages/customer/ServiceHistory";
import Analytics from "./pages/customer/Analytics";
import Preferences from "./pages/customer/Preferences";
import PaymentMethods from "./pages/customer/PaymentMethods";
import AdminPanel from "./pages/admin/AdminPanel";
import { ToastProvider } from "./components/ui/toast-provider";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

const helmetContext = {};

// Catch-all: send authenticated users into the app, everyone else to the
// public maintenance homepage.
const CatchAllRedirect: React.FC = () => {
  const { user, session, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (user && session) return <Navigate to="/app" replace />;
  // Preserve the attempted path so post-login can resume there if it was /app/*
  const from = location.pathname.startsWith("/app") ? location : undefined;
  return <Navigate to="/" state={from ? { from } : undefined} replace />;
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <NotificationProvider>
          <BookingProvider>
            <HelmetProvider context={helmetContext}>
              <TooltipProvider>
                <ToastProvider>
                  <Routes>
                    {/* Public maintenance homepage */}
                    <Route path="/" element={<Index />} />

                    {/* Login (redirects to /app if already authenticated) */}
                    <Route
                      path="/login"
                      element={
                        <AuthGuard requireAuth={false}>
                          <LoginPage />
                        </AuthGuard>
                      }
                    />

                    {/* Internal operational dashboard — active admins only */}
                    <Route
                      path="/app"
                      element={
                        <AdminGuard>
                          <CustomerPortalLayout>
                            <CustomerDashboard />
                          </CustomerPortalLayout>
                        </AdminGuard>
                      }
                    />
                    <Route
                      path="/app/schedule"
                      element={
                        <AdminGuard>
                          <CustomerPortalLayout>
                            <SchedulePage />
                          </CustomerPortalLayout>
                        </AdminGuard>
                      }
                    />
                    <Route
                      path="/app/history"
                      element={
                        <AdminGuard>
                          <CustomerPortalLayout>
                            <ServiceHistory />
                          </CustomerPortalLayout>
                        </AdminGuard>
                      }
                    />
                    <Route
                      path="/app/analytics"
                      element={
                        <AdminGuard>
                          <CustomerPortalLayout>
                            <Analytics />
                          </CustomerPortalLayout>
                        </AdminGuard>
                      }
                    />
                    <Route
                      path="/app/preferences"
                      element={
                        <AdminGuard>
                          <CustomerPortalLayout>
                            <Preferences />
                          </CustomerPortalLayout>
                        </AdminGuard>
                      }
                    />
                    <Route
                      path="/app/payment-methods"
                      element={
                        <AdminGuard>
                          <CustomerPortalLayout>
                            <PaymentMethods />
                          </CustomerPortalLayout>
                        </AdminGuard>
                      }
                    />
                    <Route
                      path="/app/profile"
                      element={
                        <AdminGuard>
                          <CustomerPortalLayout>
                            <ProfilePage />
                          </CustomerPortalLayout>
                        </AdminGuard>
                      }
                    />
                    <Route
                      path="/app/admin"
                      element={
                        <AdminGuard>
                          <AdminPanel />
                        </AdminGuard>
                      }
                    />

                    {/* Unknown route: auth-aware redirect */}
                    <Route path="*" element={<CatchAllRedirect />} />
                  </Routes>
                </ToastProvider>
              </TooltipProvider>
            </HelmetProvider>
          </BookingProvider>
        </NotificationProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
